import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import Role from "constants/role";
import { isInIframe } from "helpers/app";
import { inTrial, isPaidSubscription } from "helpers/subscription";
import { getDaysLeft } from "helpers/subscription-plans";
import { selectLoggedInAgent } from "store/entities/agents";
import { selectIsNonProfitAccount, selectLicenseId } from "store/entities/license";
import {
  selectCurrentSubscription,
  selectCurrentSubscriptionPlan,
  selectSeatsCount,
  selectSubscriptionPhase,
} from "store/entities/subscriptions";

import { useChatWidget } from "./use-chat-widget";
import { useOrganization } from "./use-organizations";

type IAccountType = "trial" | "paid" | "expired";

const NO_DATA_VALUE = "-";
const PERMISSIONS = {
  [Role.Owner]: "admin",
  [Role.Normal]: "agent",
  [Role.Viewer]: "viewer",
} as const;

export const useSetHelpDeskBotData = (): void => {
  const { setOnReadyCallback, setSessionVariables } = useChatWidget();
  const isDisplayedAsLiveChatIntegration = isInIframe();
  const { organization, isSuccess: fetchedOrganization } = useOrganization();

  const [initialized, setInitialized] = useState(false);

  const subscription = useSelector(selectCurrentSubscription);
  const subscriptionPhase = useSelector(selectSubscriptionPhase);
  const licenseId = useSelector(selectLicenseId);
  const loggedInAgent = useSelector(selectLoggedInAgent);
  const isNonProfitAccount = useSelector(selectIsNonProfitAccount);
  const seatsCount = useSelector(selectSeatsCount);
  const subscriptionPlan = useSelector(selectCurrentSubscriptionPlan);

  const isTrial = inTrial(subscription);
  const isPaid = isPaidSubscription(subscriptionPhase);
  const trialDaysLeft = getDaysLeft(subscription);

  const getAccountType = (): IAccountType => {
    if (isTrial) return "trial";
    if (isPaid) return "paid";
    return "expired";
  };

  const onChatWidgetReady = (): void => {
    setSessionVariables({
      og_cv_hd_company_size: organization.company.companySize || NO_DATA_VALUE,
      og_cv_hd_company_industry: organization.company.industry || NO_DATA_VALUE,
      og_cv_hd_account_type: getAccountType(),
      og_cv_hd_permission: PERMISSIONS[loggedInAgent.permission],
      og_cv_hd_nonprofit: isNonProfitAccount,
      og_cv_hd_license: licenseId,
      og_cv_hd_subscription_seats: seatsCount,
      og_cv_hd_subscription_plan: subscriptionPlan,
      ...(isTrial ? { og_cv_hd_day: trialDaysLeft } : {}),
    });
  };

  useEffect(() => {
    if (fetchedOrganization && !initialized && !isDisplayedAsLiveChatIntegration) {
      setOnReadyCallback(() => onChatWidgetReady());
      setInitialized(true);
    }
  }, [fetchedOrganization]);
};
